import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAllCompanies } from '../../queries/companies';
import { getAdminKnowledgeBase } from '../../queries/adminKnowledgeBase';
import {
  ALL_SOURCES,
  DEFAULT_STATS,
  PAGE_SIZE,
  buildKnowledgeSearchParams,
  clientUserIdOf,
  filterKnowledgeRows,
  getInfoBannerMessage,
  paginateRows,
  parseKnowledgeSearchParams,
  resolveClientSelection,
  sortKnowledgeRows,
} from './knowledgeBaseUtils';

const useAdminKnowledgeBase = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const tableTopRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const parsed = useMemo(() => parseKnowledgeSearchParams(searchParams), [searchParams]);

  const {
    data: companies = [],
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useQuery(getAllCompanies({}));

  const { selectedClientId, selectedCompany } = useMemo(
    () => resolveClientSelection(companies, parsed.clientId),
    [companies, parsed.clientId],
  );

  const clientUserId = useMemo(() => clientUserIdOf(selectedCompany), [selectedCompany]);

  const {
    data: knowledgeData,
    isLoading: isKnowledgeLoading,
    isError: isKnowledgeError,
  } = useQuery(getAdminKnowledgeBase({ clientId: clientUserId }));

  const knowledge = useMemo(
    () => ({
      stats: knowledgeData?.stats || DEFAULT_STATS,
      rows: knowledgeData?.rows || [],
      sources: knowledgeData?.sources || [],
    }),
    [knowledgeData],
  );

  const syncParams = useCallback(
    (next) => {
      setSearchParams(buildKnowledgeSearchParams(next), { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (isCompaniesLoading || companies.length === 0) return;
    if (String(parsed.clientId) === String(selectedClientId)) return;
    syncParams({
      clientId: selectedClientId,
      search: parsed.search,
      source: parsed.source,
      page: 1,
      sortBy: parsed.sortBy,
      sortDir: parsed.sortDir,
    });
  }, [
    companies.length,
    isCompaniesLoading,
    parsed.clientId,
    parsed.search,
    parsed.sortBy,
    parsed.sortDir,
    parsed.source,
    selectedClientId,
    syncParams,
  ]);

  // A source filter from the URL may not exist for the selected client.
  const activeSource = useMemo(() => {
    if (parsed.source === ALL_SOURCES) return ALL_SOURCES;
    const known = knowledge.sources.some((option) => String(option.id) === String(parsed.source));
    return known ? parsed.source : ALL_SOURCES;
  }, [knowledge.sources, parsed.source]);

  const filteredRows = useMemo(
    () => filterKnowledgeRows(knowledge.rows, { search: parsed.search, source: activeSource }),
    [knowledge.rows, parsed.search, activeSource],
  );

  const sortedRows = useMemo(
    () => sortKnowledgeRows(filteredRows, { sortBy: parsed.sortBy, sortDir: parsed.sortDir }),
    [filteredRows, parsed.sortBy, parsed.sortDir],
  );

  const { rows: pagedRows, pageCount, currentPage, total } = useMemo(
    () => paginateRows(sortedRows, { page: parsed.page, pageSize: PAGE_SIZE }),
    [sortedRows, parsed.page],
  );

  useEffect(() => {
    if (!selectedClientId) return;
    if (parsed.page === currentPage) return;
    syncParams({
      clientId: selectedClientId,
      search: parsed.search,
      source: parsed.source,
      page: currentPage,
      sortBy: parsed.sortBy,
      sortDir: parsed.sortDir,
    });
  }, [
    currentPage,
    parsed.page,
    parsed.search,
    parsed.sortBy,
    parsed.sortDir,
    parsed.source,
    selectedClientId,
    syncParams,
  ]);

  const updateState = (patch, { resetPage = true } = {}) => {
    syncParams({
      clientId: selectedClientId,
      search: parsed.search,
      source: parsed.source,
      page: resetPage ? 1 : parsed.page,
      sortBy: parsed.sortBy,
      sortDir: parsed.sortDir,
      ...patch,
    });
  };

  const handleClientChange = (event) => {
    syncParams({
      clientId: event.target.value,
      search: '',
      source: ALL_SOURCES,
      page: 1,
      sortBy: 'topic',
      sortDir: 'asc',
    });
  };

  const handleClear = () => {
    updateState({ search: '', source: ALL_SOURCES, page: 1 });
  };

  const handleSort = (column) => {
    const nextDir = parsed.sortBy === column && parsed.sortDir === 'asc' ? 'desc' : 'asc';
    updateState({ sortBy: column, sortDir: nextDir });
  };

  const handlePageChange = (_event, nextPage) => {
    updateState({ page: nextPage }, { resetPage: false });
    if (typeof tableTopRef.current?.scrollIntoView === 'function') {
      tableTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const hasActiveFilters = Boolean(parsed.search.trim()) || activeSource !== ALL_SOURCES;

  return {
    companies,
    isCompaniesLoading,
    isCompaniesError,
    selectedClientId,
    selectedCompany,
    knowledge,
    isKnowledgeLoading,
    isKnowledgeError,
    parsed: { ...parsed, source: activeSource },
    pagedRows,
    pageCount,
    currentPage,
    total,
    hasActiveFilters,
    bannerMessage: getInfoBannerMessage({
      hasActiveFilters,
      resultCount: total,
      totalCount: knowledge.rows.length,
    }),
    showEmptyClients: !isCompaniesLoading && !isCompaniesError && companies.length === 0,
    showKnowledge: Boolean(selectedClientId) && !isCompaniesLoading && !isCompaniesError,
    isCompact,
    isTablet,
    tableTopRef,
    handleClientChange,
    handleClear,
    handleSort,
    handlePageChange,
    updateState,
  };
};

export default useAdminKnowledgeBase;
